import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService } from 'ui-components/services/notification-service';

import { ModalPortal } from '../modal-portal';
import { PhoneNumberCaptureForm } from '../phone-number-capture/phone-number-capture-form';
import { useSavePhoneNumber } from '../phone-number-capture/use-save-phone-number';
import { PHONE_CAPTURE_SOURCE, type PhoneCaptureSource } from '../phone-number-capture/constants';
import { messages } from './messages';
import style from './phone-number-capture-modal.module.less';
import { Segment } from '../../services/analytics-services/segment';
import {
  PHONE_CAPTURE_MODAL_DISMISSED,
  PHONE_CAPTURE_MODAL_SUBMITTED,
  PHONE_CAPTURE_MODAL_SUBMIT_FAILED,
} from '../../constants/analytics-events';
import Global from '../../store/global-selectors';
import { UserSettings } from '../../enums/user-settings/user-settings';
import { saveUserSetting } from '../../store/user/user.actions';
import { US } from '../../constants/country-codes';

type PhoneNumberCaptureModalProps = {
  onClose: () => void;
  source?: PhoneCaptureSource;
};

export const PhoneNumberCaptureModal = ({ onClose, source = PHONE_CAPTURE_SOURCE.AUTO }: PhoneNumberCaptureModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const sellerCountry = useSelector(Global.selectSellerHomeCountry);
  const countryCode = sellerCountry || US;
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isConsentChecked, setIsConsentChecked] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);

  const handleSuccess = useCallback(() => {
    Segment.trackEvent(PHONE_CAPTURE_MODAL_SUBMITTED, { source, success: true });
    NotificationService.showNotification({
      notificationType: 'success',
      title: intl.formatMessage(messages.successMessage),
    });
    onClose();
  }, [intl, onClose, source]);

  const handleError = useCallback(() => {
    Segment.trackEvent(PHONE_CAPTURE_MODAL_SUBMIT_FAILED, { source });
    NotificationService.showNotification({
      notificationType: 'error',
      title: intl.formatMessage(messages.errorMessage),
    });
  }, [intl, source]);

  const { savePhoneNumber, isSaving } = useSavePhoneNumber({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handleSubmit = useCallback(async () => {
    await savePhoneNumber({
      phoneNumber,
      isConsentChecked,
      countryCode,
    });
  }, [phoneNumber, isConsentChecked, countryCode, savePhoneNumber]);

  const handleDismiss = useCallback(() => {
    Segment.trackEvent(PHONE_CAPTURE_MODAL_DISMISSED, { source });
    dispatch(
      saveUserSetting({
        settingName: UserSettings.phoneNumberCaptureModalDismissed.settingName,
        settingValue: true,
        excludeV3prefix: true,
      }),
    );
    onClose();
  }, [dispatch, onClose, source]);

  const isSaveDisabled = isSaving || !isFormValid;

  return (
    <ModalPortal onClose={handleDismiss}>
      <Modal modalHeaderId={modalHeaderId}>
        <ModalHeader className={style.modalHeader} closeModal={handleDismiss} id={modalHeaderId}>
          <Text textType={TextType.H3} value={messages.modalTitle} />
        </ModalHeader>
        <ModalBody className={style.modalBody}>
          <PhoneNumberCaptureForm
            countryCode={countryCode}
            isConsentChecked={isConsentChecked}
            isDisabled={isSaving}
            onConsentChange={setIsConsentChecked}
            onPhoneChange={setPhoneNumber}
            onValidationChange={setIsFormValid}
          />
        </ModalBody>
        <ModalFooter>
          <Button disabled={isSaving} onClick={handleDismiss} theme={THEME_CONSTANTS.white}>
            <FormattedMessage {...messages.cancelButton} />
          </Button>
          <Button disabled={isSaveDisabled} onClick={handleSubmit} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.submitButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
